import React, { useState, useEffect, useRef } from 'react';
import styles from './index.module.less';

interface VolumeIndicatorProps {
  volume: number; // 0-100
  mode?: 'relative' | 'absolute'; // relative: 相对于阈值, absolute: 绝对音量
  threshold?: number; // 阈值 (0-100)
  label?: string;
  avatarBased?: boolean; // 是否基于头像定位
  visible?: boolean; // 是否显示（用于外部控制渐入渐出）
  hideLabel?: boolean; // 是否隐藏文本标签
  onAnimationEnd?: () => void; // 动画结束回调
  isMuted?: boolean; // 是否静音
  inline?: boolean; // 是否使用内联模式（相对定位，跟随文档流）
}

const HISTORY_LENGTH = 120; // 波形点数提升

export default function VolumeIndicator({
  volume,
  mode = 'absolute',
  threshold = 20,
  label,
  avatarBased = false,
  visible = true,
  hideLabel = false,
  onAnimationEnd,
  isMuted = false,
  inline = false,
}: VolumeIndicatorProps) {
  // 统一阈值，激活和未激活时都用同一个最大阈值
  const unifiedThreshold = 20;

  // 计算显示的音量比例
  let displayVolume = 0;
  let displayLabel = label;
  if (isMuted) {
    displayVolume = 0;
    displayLabel = displayLabel || '[Volume] 麦克风已静音';
  } else if (mode === 'relative') {
    displayVolume = Math.min(100, (volume / unifiedThreshold) * 100);
    displayLabel = displayLabel || '[Volume] 语音唤醒已就绪，大声说话试试';
  } else if (mode === 'absolute') {
    displayVolume = Math.min(100, (volume / unifiedThreshold) * 100);
    displayLabel = displayLabel || '[Volume] 说话音量';
  }

  // waveHistory 初始化为当前 displayVolume，避免左侧小点和底线
  const [waveHistory, setWaveHistory] = useState<number[]>(() =>
    Array(HISTORY_LENGTH).fill(displayVolume)
  );
  const [shouldRender, setShouldRender] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const rafRef = useRef<number>();

  // 初始化时用当前音量填充数组，确保平滑过渡
  useEffect(() => {
    if (waveHistory.every((v) => v === 0) && displayVolume > 0) {
      setWaveHistory(Array(HISTORY_LENGTH).fill(displayVolume));
    }
    // eslint-disable-next-line
  }, [displayVolume]);

  // 控制显示/隐藏逻辑
  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      setIsFadingOut(false);
      return;
    }
    setIsFadingOut(true);
    const timer = setTimeout(() => {
      setShouldRender(false);
      onAnimationEnd?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [visible, onAnimationEnd]);

  // 波形流动动画，使用优化的滑动窗口算法
  useEffect(() => {
    if (!shouldRender || isMuted) {
      // 确保在不渲染时清理 RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
      return;
    }

    let running = true;
    let lastUpdate = 0;
    const updateInterval = 50; // 50ms更新间隔，减少计算频率

    function update(timestamp: number) {
      if (!running) return;

      // 限制更新频率
      if (timestamp - lastUpdate < updateInterval) {
        rafRef.current = requestAnimationFrame(update);
        return;
      }
      lastUpdate = timestamp;

      setWaveHistory((prev) => {
        // 初始化时用当前音量填充数组
        if (prev.every((v) => v === 0)) {
          return Array(HISTORY_LENGTH).fill(displayVolume);
        }

        // 优化的滑动窗口：只更新最后一个值，避免整个数组重新计算
        const newHistory = [...prev];
        newHistory.shift(); // 移除第一个元素

        // 新采样点用前一个点和当前音量做加权平均，减少突变
        const prevVal = prev[prev.length - 1] || 0;
        const noise = (Math.random() - 0.5) * 1; // 减少噪声范围
        const raw = Math.max(0, Math.min(100, displayVolume + noise));
        const smooth = prevVal * 0.6 + raw * 0.4; // 增加平滑度

        newHistory.push(smooth);
        return newHistory;
      });

      if (running) {
        rafRef.current = requestAnimationFrame(update);
      }
    }

    rafRef.current = requestAnimationFrame(update);

    // 清理函数：确保 RAF 被正确取消
    return () => {
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
    };
  }, [shouldRender, isMuted, displayVolume]);

  // 生成波浪路径（从右往左流动）
  const width = 280;
  const height = 32;
  const amplitude = 12; // 最大振幅提升
  const baseY = height / 2;
  const path = (() => {
    let d = '';
    for (let i = 0; i < HISTORY_LENGTH; i++) {
      const x = (i / (HISTORY_LENGTH - 1)) * width;
      const v = waveHistory[i];
      const y = baseY - (v / 100) * amplitude;
      if (i === 0) {
        d = `M ${x} ${y}`;
      } else {
        d += ` L ${x} ${y}`;
      }
    }
    return d;
  })();

  // 背景波浪（更淡，平滑）
  const bgPath = (() => {
    let d = '';
    for (let i = 0; i < HISTORY_LENGTH; i++) {
      const x = (i / (HISTORY_LENGTH - 1)) * width;
      const v = waveHistory[i];
      const y = baseY - (v / 100) * amplitude * 0.5;
      if (i === 0) {
        d = `M ${x} ${y}`;
      } else {
        d += ` L ${x} ${y}`;
      }
    }
    return d;
  })();

  const containerClass = [
    styles.volumeIndicator,
    inline ? styles.inline : '',
    avatarBased ? styles.avatarBased : '',
    isFadingOut ? styles.fadeOut : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!shouldRender) return null;

  return (
    <div className={containerClass}>
      {!hideLabel && <div className={styles.volumeLabel}>{displayLabel}</div>}
      <div className={styles.waveContainer}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className={styles.waveSvg}
        >
          {/* 背景波浪 */}
          <path
            d={bgPath}
            stroke={isMuted ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.18)'}
            strokeWidth="0.8"
            fill="none"
            className={styles.backgroundWave}
          />
          {/* 主波浪 */}
          <path
            d={path}
            stroke={isMuted ? 'rgba(255,255,255,0.18)' : '#8ee7f7'}
            strokeWidth="1.5"
            fill="none"
            className={styles.mainWave}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
